import React, { useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Switch,
  InputAdornment,
  Divider,
  Paper,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import DentalChart from './DentalChart';
import FileDropZone, { UploadedAttachment } from './FileDropZone';

/**
 * Renders a SOAP note template schema dynamically.
 * Schema is an array of field control definitions from the SoapTemplate.schema field.
 */

export interface TemplateFieldControl {
  key: string;
  label: string;
  // 'treatment_plan' holds no value: it marks where the note's Plan block (plan.treatments etc.) goes.
  type: 'text' | 'textarea' | 'number' | 'select' | 'checklist' | 'boolean' | 'group' | 'visual' | 'file_drop' | 'treatment_plan';
  section: 'subjective' | 'objective' | 'assessment' | 'plan';
  required?: boolean;
  placeholder?: string;
  options?: string[];       // for select / checklist
  unit?: string;            // for number
  children?: TemplateFieldControl[]; // for group
  visualType?: 'dental_chart' | 'body_diagram'; // for visual
  accept?: string;          // for file_drop
  multiple?: boolean;       // for file_drop
}

interface TemplateFormRendererProps {
  schema: TemplateFieldControl[];
  values: Record<string, any>;
  onChange: (key: string, value: any) => void;
  horseId?: string;
  soapNoteId?: string;
  onAIExtract?: (fieldKey: string, attachmentId: string, extracted: any) => void;
  section?: 'subjective' | 'objective' | 'assessment' | 'plan'; // if set, only render this section
  readOnly?: boolean;
  /** Suppress in-body `file_drop` fields. The SOAP editor centralizes ALL file
   *  uploads in one "Attachments" section at the top of the dialog, so the
   *  per-template field would be a duplicate. Read-only views leave it on. */
  hideFileDrop?: boolean;
  /** Drawn at a `treatment_plan` field: the note's Plan block, so vaccines, medications and procedures
   *  reach their records on completion. Views that show the plan elsewhere (the chart card) leave it out. */
  treatmentPlan?: React.ReactNode;
}

const SECTION_LABELS = {
  subjective: 'Subjective',
  objective: 'Objective',
  assessment: 'Assessment',
  plan: 'Plan',
};

const SECTION_COLORS = {
  subjective: '#1976d2',
  objective: '#388e3c',
  assessment: '#f57c00',
  plan: '#7b1fa2',
};

/**
 * Renders children of a group field, supporting all field types (text, number, select, checklist, etc.)
 */
const GroupChildRenderer: React.FC<{
  children: TemplateFieldControl[];
  groupValues: Record<string, any>;
  groupOnChange: (childKey: string, childVal: any) => void;
  readOnly: boolean;
}> = ({ children, groupValues, groupOnChange, readOnly }) => {
  return (
    <>
      {children.map(child => {
        const childValue = groupValues[child.key];

        // In read-only mode, skip empty child fields
        if (readOnly) {
          const empty = childValue === undefined || childValue === null || childValue === ''
            || (Array.isArray(childValue) && childValue.length === 0);
          if (empty) return null;
        }

        switch (child.type) {
          case 'number':
            return (
              <TextField
                key={child.key}
                label={child.label + (child.required ? ' *' : '')}
                fullWidth
                size="small"
                type="number"
                value={childValue ?? ''}
                onChange={e => groupOnChange(child.key, e.target.value === '' ? '' : Number(e.target.value))}
                placeholder={(child as any).placeholder}
                disabled={readOnly}
                sx={{ mb: 1.5 }}
                InputProps={child.unit ? { endAdornment: <InputAdornment position="end">{child.unit}</InputAdornment> } : undefined}
              />
            );
          case 'select':
            return (
              <FormControl key={child.key} fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel>{child.label + (child.required ? ' *' : '')}</InputLabel>
                <Select
                  value={childValue || ''}
                  label={child.label + (child.required ? ' *' : '')}
                  onChange={e => groupOnChange(child.key, e.target.value)}
                  disabled={readOnly}
                >
                  <MenuItem value=""><em>—</em></MenuItem>
                  {(child.options || []).map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            );
          case 'checklist': {
            const checkedValues = Array.isArray(childValue) ? childValue : [];
            return (
              <Box key={child.key} sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>{child.label}{child.required ? ' *' : ''}</Typography>
                <FormGroup row>
                  {(child.options || []).map(opt => (
                    <FormControlLabel
                      key={opt}
                      label={opt}
                      control={
                        <Checkbox
                          size="small"
                          checked={checkedValues.includes(opt)}
                          disabled={readOnly}
                          onChange={e => {
                            const updated = e.target.checked
                              ? [...checkedValues, opt]
                              : checkedValues.filter((v: string) => v !== opt);
                            groupOnChange(child.key, updated);
                          }}
                        />
                      }
                    />
                  ))}
                </FormGroup>
              </Box>
            );
          }
          case 'boolean':
            return (
              <Box key={child.key} sx={{ mb: 1.5 }}>
                <FormControlLabel
                  label={child.label}
                  control={
                    <Switch
                      checked={!!childValue}
                      onChange={e => groupOnChange(child.key, e.target.checked)}
                      disabled={readOnly}
                      size="small"
                    />
                  }
                />
              </Box>
            );
          default:
            // text, textarea
            return (
              <TextField
                key={child.key}
                label={child.label + (child.required ? ' *' : '')}
                fullWidth
                size="small"
                multiline={child.type === 'textarea'}
                rows={child.type === 'textarea' ? 2 : 1}
                value={childValue || ''}
                onChange={e => groupOnChange(child.key, e.target.value)}
                placeholder={(child as any).placeholder}
                disabled={readOnly}
                sx={{ mb: 1.5 }}
              />
            );
        }
      })}
    </>
  );
};

const TemplateFormRenderer: React.FC<TemplateFormRendererProps> = React.memo(({
  schema,
  values,
  onChange,
  horseId,
  soapNoteId,
  onAIExtract,
  section,
  readOnly = false,
  hideFileDrop = false,
  treatmentPlan,
}) => {
  const handleChange = useCallback((key: string, value: any) => {
    onChange(key, value);
  }, [onChange]);

  const isFieldEmpty = (val: any): boolean => {
    if (val === undefined || val === null || val === '') return true;
    if (Array.isArray(val) && val.length === 0) return true;
    if (typeof val === 'object' && !Array.isArray(val) && Object.keys(val).length === 0) return true;
    return false;
  };

  const renderField = (field: TemplateFieldControl, depth = 0): React.ReactNode => {
    const fieldValue = values[field.key];

    // Its rows live in the note's plan, not in `values`, so the empty-field rule below never applies. Its own
    // headings name it, so the stored label is never shown.
    if (field.type === 'treatment_plan') {
      if (!treatmentPlan) return null;
      return <Box key={field.key} sx={{ mb: 2 }}>{treatmentPlan}</Box>;
    }

    // In read-only mode, hide fields that have no value
    if (readOnly && field.type !== 'group') {
      if (isFieldEmpty(fieldValue)) return null;
    }
    if (readOnly && field.type === 'group' && field.children) {
      const groupVals = (fieldValue as Record<string, any>) || {};
      const hasAnyValue = field.children.some(c => !isFieldEmpty(groupVals[c.key]));
      if (!hasAnyValue) return null;
    }

    switch (field.type) {
      case 'text':
        return (
          <TextField
            key={field.key}
            label={field.label + (field.required ? ' *' : '')}
            fullWidth
            size="small"
            value={fieldValue || ''}
            onChange={e => handleChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            disabled={readOnly}
            sx={{ mb: 2 }}
            InputProps={field.unit ? {
              endAdornment: <InputAdornment position="end">{field.unit}</InputAdornment>,
            } : undefined}
          />
        );

      case 'textarea':
        return (
          <TextField
            key={field.key}
            label={field.label + (field.required ? ' *' : '')}
            fullWidth
            size="small"
            multiline
            rows={3}
            value={fieldValue || ''}
            onChange={e => handleChange(field.key, e.target.value)}
            placeholder={field.placeholder}
            disabled={readOnly}
            sx={{ mb: 2 }}
          />
        );

      case 'number':
        return (
          <TextField
            key={field.key}
            label={field.label + (field.required ? ' *' : '')}
            fullWidth
            size="small"
            type="number"
            value={fieldValue ?? ''}
            onChange={e => handleChange(field.key, e.target.value === '' ? '' : Number(e.target.value))}
            placeholder={field.placeholder}
            disabled={readOnly}
            sx={{ mb: 2 }}
            InputProps={field.unit ? {
              endAdornment: <InputAdornment position="end">{field.unit}</InputAdornment>,
            } : undefined}
          />
        );

      case 'select':
        return (
          <FormControl fullWidth size="small" sx={{ mb: 2 }} key={field.key}>
            <InputLabel>{field.label + (field.required ? ' *' : '')}</InputLabel>
            <Select
              value={fieldValue || ''}
              label={field.label + (field.required ? ' *' : '')}
              onChange={e => handleChange(field.key, e.target.value)}
              disabled={readOnly}
            >
              <MenuItem value=""><em>—</em></MenuItem>
              {(field.options || []).map(opt => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'checklist': {
        const checkedValues: string[] = Array.isArray(fieldValue) ? fieldValue : [];
        return (
          <Box key={field.key} sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {field.label}{field.required ? ' *' : ''}
            </Typography>
            <FormGroup>
              {(field.options || []).map(opt => (
                <FormControlLabel
                  key={opt}
                  control={
                    <Checkbox
                      size="small"
                      checked={checkedValues.includes(opt)}
                      disabled={readOnly}
                      onChange={e => {
                        const updated = e.target.checked
                          ? [...checkedValues, opt]
                          : checkedValues.filter(v => v !== opt);
                        handleChange(field.key, updated);
                      }}
                    />
                  }
                  label={<Typography variant="body2">{opt}</Typography>}
                />
              ))}
            </FormGroup>
          </Box>
        );
      }

      case 'boolean':
        return (
          <Box key={field.key} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!fieldValue}
                  onChange={e => handleChange(field.key, e.target.checked)}
                  disabled={readOnly}
                  size="small"
                />
              }
              label={<Typography variant="body2">{field.label}{field.required ? ' *' : ''}</Typography>}
            />
          </Box>
        );

      case 'group': {
        const groupValues = (fieldValue as Record<string, any>) || {};
        // Wrap onChange to nest child values under the group key
        const groupOnChange = (childKey: string, childVal: any) => {
          handleChange(field.key, { ...groupValues, [childKey]: childVal });
        };
        return (
          <Box key={field.key} sx={{ mb: 2 }}>
            <Accordion defaultExpanded={depth === 0} disableGutters>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, bgcolor: 'grey.50' }}>
                <Typography variant="body2" fontWeight={600}>{field.label}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 1.5 }}>
                <GroupChildRenderer
                  children={field.children || []}
                  groupValues={groupValues}
                  groupOnChange={groupOnChange}
                  readOnly={readOnly}
                />
              </AccordionDetails>
            </Accordion>
          </Box>
        );
      }

      case 'visual': {
        const vType = field.visualType || 'dental_chart';
        if (vType === 'dental_chart') {
          return (
            <Box key={field.key} sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {field.label}{field.required ? ' *' : ''}
              </Typography>
              <DentalChart
                value={fieldValue || {}}
                onChange={v => handleChange(field.key, v)}
                readOnly={readOnly}
              />
            </Box>
          );
        }
        return null;
      }

      case 'file_drop':
        // Centralized in the dialog's top "Attachments" section — don't render a
        // duplicate per-field dropzone in the editor.
        if (hideFileDrop) return null;
        return (
          <Box key={field.key} sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              {field.label}{field.required ? ' *' : ''}
            </Typography>
            <FileDropZone
              horseId={horseId}
              parentType={soapNoteId ? 'soap_note' : undefined}
              parentId={soapNoteId}
              fieldKey={field.key}
              accept={field.accept}
              multiple={field.multiple}
              value={Array.isArray(fieldValue) ? fieldValue : []}
              onChange={attachments => handleChange(field.key, attachments)}
              onAIExtract={onAIExtract ? (id, extracted) => onAIExtract(field.key, id, extracted) : undefined}
              templateSchema={schema}
              readOnly={readOnly}
            />
          </Box>
        );

      default:
        return null;
    }
  };

  // Group fields by section
  const sections = ['subjective', 'objective', 'assessment', 'plan'] as const;
  const visibleSections = section ? [section] : sections;

  return (
    <Box>
      {visibleSections.map(sec => {
        const fields = schema.filter(f => f.section === sec);
        if (!fields.length) return null;
        return (
          <Box key={sec} sx={{ mb: 3 }}>
            {!section && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Box sx={{ width: 4, height: 20, bgcolor: SECTION_COLORS[sec], borderRadius: 1 }} />
                <Typography variant="subtitle1" fontWeight={700} color={SECTION_COLORS[sec]}>
                  {SECTION_LABELS[sec]}
                </Typography>
                <Chip
                  label={`${fields.filter(f => f.required).length} required`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '10px', height: 18 }}
                />
              </Box>
            )}
            {fields.map(field => renderField(field))}
            {!section && sec !== 'plan' && <Divider sx={{ mt: 1 }} />}
          </Box>
        );
      })}
    </Box>
  );
});

TemplateFormRenderer.displayName = 'TemplateFormRenderer';

export default TemplateFormRenderer;
